/**
 * Update date: 2024-07-31
 * Screen 2088.1
 */
import { DataGridFull } from 'app/components/DataGrid';
import LoadPanel from 'app/components/LoadPanel';
import useAxios from 'axios-hooks';
import useFormat from 'hooks/useFormat';
import useFormatTime from 'hooks/useFormatTime';
import moment from 'moment';
import { memo, useRef, useState } from 'react';
import PopupDetail from '../PopupDetail';
import { notification } from 'utils/notification';
import PopupOrder from 'app/components/PopupCommon/PopupOrder';

const ForwarDetailTab = ({ t, dataDetail }) => {
  const dataGridRef: any = useRef(null);
  const { DateTimeFormat } = useFormatTime();
  const { AmountFormat } = useFormat();
  const detailRef: any = useRef(null);
  const [showPopupOrder, setShowPopupOrder] = useState<any>({
    visible: false,
    orderId: null,
  });

  const [{ loading }, refetchData] = useAxios(
    {},
    { manual: true, autoCancel: true, useCache: false },
  );

  const onRecollectionOrderData = async (mallCd, orderNumber) => {
    const data = {
      mallCd: mallCd,
      orderNumber: orderNumber,
    };
    const res = await refetchData({
      url: '/ecommerce/order-popup/send-recollection',
      method: 'POST',
      data: data,
    });
    notification({ res });
  };

  /**
   * on export excel
   *
   */
  const exportExcel = () => {
    dataGridRef?.current?.instance?.exportToExcel();
  };

  const columns: any = [
    {
      dataField: 'orderDt',
      caption: t('Order Date'),
      dataType: 'date',
      format: DateTimeFormat,
    },
    {
      dataField: 'mallOrderId',
      caption: t('Order Number'),
      cellRender: e => {
        const orderId = e?.data?.orderId;
        return orderId ? (
          <div
            style={{ color: 'brown', cursor: 'pointer' }}
            onClick={() => {
              setShowPopupOrder({
                visible: true,
                orderId: orderId,
              });
            }}
          >
            {e?.value}
          </div>
        ) : (
          <div>{e?.value}</div>
        );
      },
    },
    {
      dataField: 'mallPaymentAmount',
      caption: t('Order Amount'),
      format: AmountFormat,
      dataType: 'number',
    },
    {
      dataField: 'forwardAmount',
      caption: t('Forward Amount'),
      format: AmountFormat,
      dataType: 'number',
    },
    {
      caption: t('Action'),
      dataField: 'action_column',
      alignment: 'left',
      cellRender: record => {
        const { mallOrderId, isDetail, mallCd } = record?.data || {};
        return (
          <div>
            <span
              onClick={() => onRecollectionOrderData(mallCd, mallOrderId)}
              className="cell-link"
            >
              {t('Order Re-collection')}
            </span>
            <span style={{ marginRight: 10, marginLeft: 10 }}>|</span>
            {isDetail ? (
              <>
                <span
                  onClick={() => detailRef?.current?.onOpen?.(mallOrderId)}
                  className="cell-link"
                >
                  {t('Forward Detail')}
                </span>
              </>
            ) : (
              <span>{t('Forward Detail')}</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <DataGridFull
        ref={dataGridRef}
        dataSource={dataDetail}
        columns={columns}
        fixedLeft={2}
        fixedRight={2}
        options={{
          columnAutoWidth: true,
          export: {
            fileName: `2088.1-Forward Details (No PG details)_${moment().format(
              'YYYYMMDD',
            )}`,
          },
          onCellClick: e => {
            if (
              e?.rowType === 'data' &&
              e?.column?.dataField === 'action_column'
            ) {
              e?.event?.preventDefault();
              e?.event?.stopPropagation();
              e?.event?.stopImmediatePropagation?.();
            }
          },
          onToolbarPreparing: (e: any) => {
            e.toolbarOptions.items.unshift(
              {
                location: 'before',
                template: 'totalCount',
              },
              {
                location: 'before',
                widget: 'dxButton',
                options: {
                  icon: 'xlsxfile',
                  text: t('Export Excel'),
                  disabled: !dataDetail?.length,
                  onClick: () => exportExcel(),
                },
              },
            );
          },
          summary: {
            totalItems: [
              {
                column: 'mallPaymentAmount',
                summaryType: 'sum',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
              {
                column: 'forwardAmount',
                summaryType: 'sum',
                valueFormat: AmountFormat,
                displayFormat: '{0}',
                dataType: 'number',
              },
            ],
          },
        }}
      />
      <LoadPanel visible={loading} />
      <PopupDetail ref={detailRef} />
      {showPopupOrder?.visible && showPopupOrder?.orderId && (
        <PopupOrder
          orderId={showPopupOrder?.orderId}
          onClose={() => {
            setShowPopupOrder({ visible: false, orderId: null });
          }}
        />
      )}
    </div>
  );
};

export default memo(ForwarDetailTab);
